import { useState } from 'react';
import {
  X, Wrench, Boxes, Settings, Droplet, Wind, Activity, ChevronRight, Siren, AlertTriangle,
} from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { usedSlotsForInstalls } from '@/data/vehicleModules';
import { stationAllowsRepair } from '@/data/stations';
import { VehicleImage } from '@/components/game/vehicles/VehicleImage';
import { silhouetteCategoryFor } from '@/components/game/vehicles/silhouettes/category';
import { WorkshopDialog } from '@/components/game/WorkshopDialog';
import { ModuleConfigDialog } from '@/components/game/ModuleConfigDialog';
import {
  REPAIR_DISABLE_THRESHOLD,
  REPAIR_SLOW_THRESHOLD,
} from '@/data/repair';
import type { Vehicle, VehicleStatus, Station } from '@/state/types';

interface VehicleDetailProps {
  vehicle: Vehicle;
  station: Station;
  onClose: () => void;
}

const STATUS_LABEL: Record<VehicleStatus, string> = {
  1: 'Frei auf Funk',
  2: 'Einsatzbereit auf Wache',
  3: 'Auf Anfahrt',
  4: 'Vor Ort',
  5: 'Sprechwunsch',
  6: 'Außer Dienst',
  7: 'Patient an Bord – frei',
  8: 'Patient an Bord – belegt',
};

const STATUS_PILL: Record<VehicleStatus, string> = {
  1: 'bg-status-available/20 text-status-available',
  2: 'bg-status-available/20 text-status-available',
  3: 'bg-status-enroute/20 text-status-enroute',
  4: 'bg-status-onscene/20 text-status-onscene',
  5: 'bg-accent-dispatch/20 text-accent-dispatch',
  6: 'bg-status-offduty/20 text-status-offduty',
  7: 'bg-status-transport/20 text-status-transport',
  8: 'bg-status-transport/20 text-status-transport',
};

const SILHOUETTE_COLOR: Record<'fire' | 'ems' | 'police' | 'special', string> = {
  fire: 'text-accent-emergency',
  ems: 'text-amber-500',
  police: 'text-blue-500',
  special: 'text-fg-muted',
};

type Tab = 'detail' | 'settings';

export function VehicleDetail({ vehicle, station, onClose }: VehicleDetailProps) {
  const spec = VEHICLE_SPECS[vehicle.type];
  const [tab, setTab] = useState<Tab>('detail');
  const [workshopOpen, setWorkshopOpen] = useState(false);
  const [moduleConfigOpen, setModuleConfigOpen] = useState(false);
  const missions = useGameStore((s) => s.missions);
  const updateVehicle = useGameStore((s) => s.updateVehicle);
  const pushToast = useGameStore((s) => s.pushToast);

  const inMission =
    vehicle.status === 3 ||
    vehicle.status === 4 ||
    vehicle.status === 7 ||
    vehicle.status === 8;
  const currentMission = vehicle.assignedMissionId
    ? missions.find((m) => m.id === vehicle.assignedMissionId)
    : null;

  // Resource displays — Block B reads from the actual vehicle state
  const waterMax = vehicle.waterTankMax;
  const water = vehicle.waterTankCurrent;
  const breathingMax = vehicle.breathingAirMax;
  const breathing = vehicle.breathingAirCurrent;
  const condition = vehicle.condition;

  const cat = silhouetteCategoryFor(vehicle.type);

  const handleRecall = () => {
    updateVehicle(vehicle.id, {
      status: 2,
      routeCoords: undefined,
      routeProgress: undefined,
      assignedMissionId: undefined,
      destination: undefined,
      position: station.position,
    });
    pushToast({
      kind: 'info',
      text: `${spec.label} zurückgerufen.`,
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Red header banner */}
      <header className="flex items-center gap-3 bg-accent-emergency px-5 py-4 text-white">
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white font-display text-xl font-bold text-accent-emergency">
          {vehicle.status}
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="font-display text-lg font-bold">
            {vehicle.displayName?.trim() || spec.label}
          </p>
          {/* Phase 11.3.2 — Rufzeichen klein darunter, falls gesetzt. */}
          {vehicle.callsign && (
            <p className="truncate text-xs font-medium opacity-90">
              📻 {vehicle.callsign}
            </p>
          )}
          <p className="truncate text-xs opacity-80">
            {spec.label} · {spec.fullName}
            {vehicle.used ? ' · gebraucht' : ''} · {vehicle.id.slice(-6)}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <HeaderIconBtn
            icon={Wrench}
            label="Werkstatt"
            onClick={() => setWorkshopOpen(true)}
          />
          <HeaderIconBtn
            icon={Boxes}
            label={
              !station
                ? 'Module'
                : !stationAllowsRepair(station)
                  ? 'Module (Werkstatt fehlt)'
                  : 'Module'
            }
            onClick={() => setModuleConfigOpen(true)}
          />
          <HeaderIconBtn
            icon={Settings}
            label="Einstellungen"
            onClick={() => setTab('settings')}
          />
          <HeaderIconBtn icon={X} label="Schließen" onClick={onClose} />
        </div>
      </header>

      {/* Phase 12.2.2 — Image banner (PNG if available, silhouette fallback). */}
      <div className="flex items-center justify-center border-b border-border-subtle bg-bg-primary px-6 py-4">
        <VehicleImage
          type={vehicle.type}
          className={['h-20 w-auto object-contain', SILHOUETTE_COLOR[cat]].join(' ')}
          silhouetteProps={{ className: ['h-16 w-auto', SILHOUETTE_COLOR[cat]].join(' ') }}
        />
      </div>

      {/* Tabs */}
      <nav className="border-b border-border-subtle bg-bg-secondary px-5 pt-3">
        <div className="flex gap-1">
          {(['detail', 'settings'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={[
                'relative px-4 py-2 text-xs font-semibold capitalize transition',
                tab === t ? 'text-fg-primary' : 'text-fg-muted hover:text-fg-secondary',
              ].join(' ')}
            >
              {t === 'detail' ? 'Detail' : 'Einstellungen'}
              {tab === t && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-t bg-accent-emergency" />
              )}
            </button>
          ))}
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5">
        {tab === 'detail' && (
          <div className="space-y-4">
            <RepairProgress vehicle={vehicle} />
            <ConditionWarning
              vehicle={vehicle}
              onOpenWorkshop={() => setWorkshopOpen(true)}
            />
            <AlarmBanner vehicle={vehicle} />
            <WorkshopBanner
              vehicle={vehicle}
              onCancel={() =>
                useGameStore.getState().cancelModuleSwap(vehicle.id)
              }
            />
            <TransitProgress vehicle={vehicle} />

            {/* Status card */}
            <Card>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
                  <Activity className="h-4 w-4 text-fg-muted" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-fg-muted">Status</p>
                  <p className="text-sm font-semibold">{STATUS_LABEL[vehicle.status]}</p>
                </div>
                <span
                  className={[
                    'rounded-full px-2.5 py-1 text-[10px] font-bold',
                    STATUS_PILL[vehicle.status],
                  ].join(' ')}
                >
                  {vehicle.status}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border-subtle pt-3 text-xs">
                <span className="text-fg-muted">Wache</span>
                <span className="font-semibold">{station.name}</span>
              </div>
            </Card>

            {/* Current mission */}
            {currentMission && (
              <button
                type="button"
                onClick={() =>
                  useGameStore.getState().selectDetail({ kind: 'mission', id: currentMission.id })
                }
                className="block w-full rounded-lg border border-accent-emergency/40 bg-accent-emergency/10 p-4 text-left transition hover:bg-accent-emergency/15"
              >
                <p className="text-[10px] uppercase tracking-wider text-accent-emergency">
                  Aktuelle Mission
                </p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="truncate font-display text-sm font-bold text-fg-primary">
                    {currentMission.title}
                  </p>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-accent-emergency" aria-hidden />
                </div>
              </button>
            )}

            {/* Recall button */}
            {inMission && (
              <button
                type="button"
                onClick={handleRecall}
                className="w-full rounded-md bg-accent-emergency px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-accent-emergency-hover"
              >
                Fahrzeug zurückrufen
              </button>
            )}

            {/* Resources */}
            {waterMax > 0 && (
              <ResourceCard
                icon={Droplet}
                iconClass="text-accent-dispatch"
                label="Wasserstand"
                current={water}
                max={waterMax}
                unit="L"
              />
            )}

            {breathingMax > 0 && (
              <BreathingAirCard current={breathing} max={breathingMax} />
            )}

            <ConditionCard value={condition} />

            <MileageCard km={vehicle.totalKilometers ?? 0} />

            {/* Phase 11.2.5 (Bug N8) — used = sum of slots for ALL installed
                modules (incl. defaults). Each variant carries its own slot
                count via the ModuleFamily catalog. */}
            <ModulesCard
              slots={spec.moduleSlots}
              used={usedSlotsForInstalls(
                (vehicle.installedModules ?? []).map((m) => m.moduleId),
              )}
            />
          </div>
        )}

        {tab === 'settings' && (
          <div className="rounded-lg border border-dashed border-border-strong p-6 text-center">
            <Settings className="mx-auto mb-3 h-6 w-6 text-fg-muted" aria-hidden />
            <p className="font-semibold">Einstellungen</p>
            <p className="mt-1 text-xs text-fg-muted">
              Fahrzeug-spezifische Konfiguration ist auf Spielebene unter „Einstellungen"
              im Burger-Menü zentralisiert.
            </p>
          </div>
        )}
      </div>

      <WorkshopDialog
        vehicle={workshopOpen ? vehicle : null}
        onClose={() => setWorkshopOpen(false)}
      />
      <ModuleConfigDialog
        vehicle={moduleConfigOpen ? vehicle : null}
        onClose={() => setModuleConfigOpen(false)}
      />
    </div>
  );
}

function HeaderIconBtn({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Settings;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-md bg-white/15 transition hover:bg-white/25"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-border-subtle bg-bg-elevated/40 p-4">{children}</div>;
}

interface ResourceCardProps {
  icon: typeof Droplet;
  iconClass: string;
  label: string;
  current: number;
  max: number;
  unit: string;
}

function ResourceCard({ icon: Icon, iconClass, label, current, max, unit }: ResourceCardProps) {
  const pct = max === 0 ? 0 : Math.min(1, current / max);
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <Icon className={['h-4 w-4', iconClass].join(' ')} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</p>
          <p className="text-sm font-semibold tabular-nums">
            {current.toLocaleString('de-DE')} {unit} / {max.toLocaleString('de-DE')} {unit}
          </p>
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-bg-primary">
        <div
          className="h-full rounded-full bg-accent-dispatch transition-all"
          style={{ width: `${Math.round(pct * 100)}%` }}
        />
      </div>
    </Card>
  );
}

function BreathingAirCard({ current, max }: { current: number; max: number }) {
  // Bottle visualisation: 8 cylinders. filled = round((current/max) * 8)
  const filled = max === 0 ? 0 : Math.round((current / max) * 8);
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <Wind className="h-4 w-4 text-status-available" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">Atemluft</p>
          <p className="text-sm font-semibold tabular-nums">
            {current.toLocaleString('de-DE')} L / {max.toLocaleString('de-DE')} L
          </p>
        </div>
        <span className="text-xs font-semibold text-fg-muted tabular-nums">{filled}/8</span>
      </div>
      <div className="mt-3 flex gap-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <span
            key={i}
            className={[
              'h-6 flex-1 rounded',
              i < filled ? 'bg-status-available' : 'bg-bg-primary',
            ].join(' ')}
            aria-hidden
          />
        ))}
      </div>
    </Card>
  );
}

function ConditionCard({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone =
    value >= 0.6
      ? 'bg-status-available'
      : value >= 0.3
        ? 'bg-amber-500'
        : 'bg-accent-emergency';
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <Activity className="h-4 w-4 text-fg-muted" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">Kondition</p>
          <p className="text-sm font-semibold tabular-nums">{pct} %</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-bg-primary">
        <div
          className={['h-full rounded-full transition-all', tone].join(' ')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Card>
  );
}

function MileageCard({ km }: { km: number }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <Activity className="h-4 w-4 text-fg-muted" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">Laufleistung</p>
          <p className="text-sm font-semibold tabular-nums">
            {Math.round(km).toLocaleString('de-DE')} km
          </p>
        </div>
      </div>
    </Card>
  );
}

function ModulesCard({ slots, used }: { slots: number; used: number }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <Boxes className="h-4 w-4 text-fg-muted" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">Fahrzeugmodule</p>
          <p className="text-sm font-semibold tabular-nums">
            {used} / {slots} verbaut
          </p>
        </div>
        <span className="rounded-full bg-bg-elevated px-2 py-1 text-[10px] text-fg-muted">
          Phase 6
        </span>
      </div>
    </Card>
  );
}

/**
 * Phase 12.3.2 — Banner während ein Modul-Tausch in der Werkstatt läuft.
 * Zeigt Countdown + "Abbrechen"-Button.
 */
function WorkshopBanner({
  vehicle,
  onCancel,
}: {
  vehicle: Vehicle;
  onCancel: () => void;
}) {
  const gameTime = useGameStore((s) => s.gameTime);
  if (!vehicle.workshopUntilAt || !vehicle.pendingModuleSwap) return null;
  const remainingMs = Math.max(0, vehicle.workshopUntilAt - gameTime);
  const remainingSec = Math.ceil(remainingMs / 1000);
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
      <p className="flex items-center gap-2 text-xs font-semibold text-amber-200">
        <Wrench className="h-3.5 w-3.5" aria-hidden /> Modul-Tausch läuft — noch{' '}
        {remainingSec >= 60
          ? `${Math.ceil(remainingSec / 60)} min`
          : `${remainingSec} s`}
      </p>
      <p className="mt-1 text-[11px] text-amber-100/80">
        Fahrzeug ist während des Tauschs nicht einsatzbereit.
      </p>
      <button
        type="button"
        onClick={onCancel}
        className="mt-2 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-bg-primary transition hover:bg-amber-500"
      >
        Tausch abbrechen
      </button>
    </div>
  );
}

/**
 * Red blinking countdown banner — shown only while the vehicle is in the
 * alarm phase (Status 2 with alarmStartedAt set, before Status 3).
 */
function AlarmBanner({ vehicle }: { vehicle: Vehicle }) {
  const gameTime = useGameStore((s) => s.gameTime);
  if (
    vehicle.alarmStartedAt === undefined ||
    vehicle.alarmCompleteAt === undefined ||
    vehicle.status !== 2
  ) {
    return null;
  }
  const remaining = Math.max(0, vehicle.alarmCompleteAt - gameTime);
  const total = Math.max(1, vehicle.alarmCompleteAt - vehicle.alarmStartedAt);
  const elapsedFraction = 1 - remaining / total;
  return (
    <div className="overflow-hidden rounded-lg border border-accent-emergency/60 bg-accent-emergency/10">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="flex h-9 w-9 flex-shrink-0 animate-pulse items-center justify-center rounded-full bg-accent-emergency text-white">
          <Siren className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-accent-emergency">
            Alarmierung
          </p>
          <p className="font-display text-sm font-bold">Mannschaft rückt aus …</p>
        </div>
        <span className="font-display text-2xl font-bold tabular-nums text-accent-emergency">
          {Math.ceil(remaining / 1000)} s
        </span>
      </div>
      <div className="h-1 w-full bg-accent-emergency/20">
        <div
          className="h-full bg-accent-emergency transition-all"
          style={{ width: `${Math.round(elapsedFraction * 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Compact progress strip for transit / on-scene work, showing remaining time.
 */
function TransitProgress({ vehicle }: { vehicle: Vehicle }) {
  const gameTime = useGameStore((s) => s.gameTime);
  const isTransit = vehicle.status === 1 || vehicle.status === 3 || vehicle.status === 7;
  const isHandover = vehicle.status === 8;
  if (!isTransit && !isHandover) return null;
  if (vehicle.legStartedAt === undefined || vehicle.legDurationMs === undefined) return null;

  const elapsed = gameTime - vehicle.legStartedAt;
  const total = vehicle.legDurationMs;
  const remaining = Math.max(0, total - elapsed);
  const pct = Math.max(0, Math.min(1, elapsed / total));

  const label =
    vehicle.status === 3
      ? 'Anfahrt zum Einsatzort'
      : vehicle.status === 7
        ? 'Patiententransport zum Krankenhaus'
        : vehicle.status === 1
          ? 'Rückfahrt zur Wache'
          : 'Übergabe Patient';

  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <Activity className="h-4 w-4 text-accent-dispatch" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</p>
          <p className="text-sm font-semibold tabular-nums">
            Noch ca. {formatRemaining(remaining)}
          </p>
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-bg-primary">
        <div
          className="h-full rounded-full bg-accent-dispatch transition-all"
          style={{ width: `${Math.round(pct * 100)}%` }}
        />
      </div>
    </Card>
  );
}

function formatRemaining(ms: number): string {
  if (ms < 1000) return '< 1 s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.floor(sec / 60);
  const rest = sec % 60;
  return rest === 0 ? `${min} min` : `${min} min ${rest} s`;
}

function ConditionWarning({
  vehicle,
  onOpenWorkshop,
}: {
  vehicle: Vehicle;
  onOpenWorkshop: () => void;
}) {
  const conditionPct = Math.round(vehicle.condition * 100);
  const isCritical = vehicle.condition <= REPAIR_DISABLE_THRESHOLD;
  const isReduced =
    !isCritical && vehicle.condition < REPAIR_SLOW_THRESHOLD;
  if (vehicle.repairStartedAt !== undefined) return null;
  if (!isCritical && !isReduced) return null;

  return (
    <div
      className={[
        'flex items-start gap-3 rounded-lg border p-3',
        isCritical
          ? 'border-accent-emergency/60 bg-accent-emergency/10'
          : 'border-amber-500/40 bg-amber-500/10',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
          isCritical
            ? 'bg-accent-emergency/30 text-accent-emergency'
            : 'bg-amber-500/30 text-amber-200',
        ].join(' ')}
      >
        {isCritical ? <AlertTriangle className="h-4 w-4" aria-hidden /> : <Wrench className="h-4 w-4" aria-hidden />}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={[
            'font-display text-sm font-bold',
            isCritical ? 'text-accent-emergency' : 'text-amber-200',
          ].join(' ')}
        >
          {isCritical ? 'Kondition kritisch' : 'Kondition reduziert'}
        </p>
        <p className="mt-0.5 text-xs text-fg-muted">
          {isCritical
            ? `Bei ${conditionPct} % nicht alarmierbar — Reparatur erforderlich.`
            : `Bei ${conditionPct} % fährt das Fahrzeug 10 % langsamer.`}
        </p>
      </div>
      <button
        type="button"
        onClick={onOpenWorkshop}
        className={[
          'rounded-md px-3 py-1.5 text-xs font-semibold text-white transition',
          isCritical
            ? 'bg-accent-emergency hover:bg-accent-emergency-hover'
            : 'bg-amber-500 hover:bg-amber-600',
        ].join(' ')}
      >
        Werkstatt
      </button>
    </div>
  );
}

function RepairProgress({ vehicle }: { vehicle: Vehicle }) {
  const gameTime = useGameStore((s) => s.gameTime);
  if (vehicle.repairStartedAt === undefined || vehicle.repairCompleteAt === undefined) {
    return null;
  }
  const elapsed = gameTime - vehicle.repairStartedAt;
  const total = vehicle.repairCompleteAt - vehicle.repairStartedAt;
  const remaining = Math.max(0, total - elapsed);
  const pct = Math.max(0, Math.min(1, elapsed / total));
  return (
    <div className="overflow-hidden rounded-lg border border-amber-500/60 bg-amber-500/10">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="flex h-9 w-9 flex-shrink-0 animate-pulse items-center justify-center rounded-full bg-amber-500 text-white">
          <Wrench className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-amber-300">In Reparatur</p>
          <p className="font-display text-sm font-bold text-amber-100">
            Fertig in {formatRemaining(remaining)}
          </p>
        </div>
      </div>
      <div className="h-1 w-full bg-amber-500/20">
        <div
          className="h-full bg-amber-400 transition-all"
          style={{ width: `${Math.round(pct * 100)}%` }}
        />
      </div>
    </div>
  );
}
